import React, { useEffect, useMemo, useState } from 'react';
import { Offcanvas, Card, Button, Form, Row, Col } from 'react-bootstrap';

const MAX_LEVELS = 4;
const ROOT_KEY = '__root__';

function safeTrim(v) {
  return String(v || '').trim();
}

function FSCLevelPickerRow({
  label,
  mode,
  options,
  selectedKey,
  inputValue,
  onSelectChange,
  onInputChange,
  onConfirmAddOption,
  showLevelActions,
  disableAddLevel,
  disableRemoveLevel,
  onAddLevel,
  onRemoveLevel,
}) {
  const [showAddMore, setShowAddMore] = useState(false);
  const [newOptionName, setNewOptionName] = useState('');

  const confirmAddMore = () => {
    const v = safeTrim(newOptionName);
    if (!v) return;
    onConfirmAddOption(v);
    setNewOptionName('');
    setShowAddMore(false);
  };

  const cancelAddMore = () => {
    setNewOptionName('');
    setShowAddMore(false);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Row className="align-items-start">
        <Col>
          <Form.Label style={{ fontWeight: 'bold' }}>{label}</Form.Label>

          {mode === 'select' ? (
            <>
              <Form.Select value={selectedKey} onChange={(e) => onSelectChange(e.target.value)}>
                <option value="">請選擇</option>
                {(options || []).map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>

              {!showAddMore ? (
                <div style={{ marginTop: 6 }}>
                  <Button
                    variant=""
                    size="sm"
                    style={{ padding: 0, textDecoration: 'none', fontWeight: 'bold' }}
                    onClick={() => setShowAddMore(true)}
                  >
                    + 新增更多選項
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Row className="g-2 align-items-center">
                    <Col>
                      <Form.Control
                        value={newOptionName}
                        onChange={(e) => setNewOptionName(e.target.value)}
                        placeholder={`新增${label}選項`}
                      />
                    </Col>
                    <Col xs="auto">
                      <Button variant="secondary" onClick={cancelAddMore}>
                        取消
                      </Button>
                    </Col>
                    <Col xs="auto">
                      <Button variant="primary" onClick={confirmAddMore} disabled={!safeTrim(newOptionName)}>
                        確定
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          ) : (
            <Form.Control value={inputValue} onChange={(e) => onInputChange(e.target.value)} placeholder={`請輸入${label}`} />
          )}
        </Col>

        {showLevelActions && (
          <Col xs="auto" style={{ marginTop: 28 }}>
            <div className="d-flex" style={{ gap: 8 }}>
              <Button variant="outline-primary" onClick={onAddLevel} disabled={disableAddLevel}>
                新增階層
              </Button>
              <Button variant="outline-secondary" onClick={onRemoveLevel} disabled={disableRemoveLevel}>
                刪除階層
              </Button>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}

function newTempId() {
  return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getNodeByPath({ rootOptions, pathKeys }) {
  let current = null;
  let currentOptions = rootOptions;

  for (const k of pathKeys) {
    const found = (currentOptions || []).find((o) => o.key === k);
    if (!found) return null;
    current = found;
    const children = Array.isArray(found?.node?.children) ? found.node.children : [];
    currentOptions = children.map((n) => ({
      key: String(n.control_id),
      label: `${String(n.control_id || '')} ${String(n.control_name || '')}`.trim(),
      node: n,
    }));
  }
  return current;
}

function parseNewKey(k) {
  // new:{level}:{value}
  const s = String(k || '');
  if (!s.startsWith('new:')) return null;
  const parts = s.split(':');
  if (parts.length < 3) return null;
  const level = Number(parts[1]);
  const value = parts.slice(2).join(':');
  return { level, value };
}

export default function FSCAddClauseItemSidebar({ show, onHide, rootOptions, onSubmit, initialDraft }) {
  const isEditing = Boolean(initialDraft);

  const [activeLevels, setActiveLevels] = useState(1);

  const [levelState, setLevelState] = useState(() =>
    Array.from({ length: MAX_LEVELS }, () => ({
      mode: 'select',
      selectedKey: '',
      inputValue: '',
    }))
  );

  const [customChildrenByParent, setCustomChildrenByParent] = useState({});

  // 格式：{ client_key, control_name, parent_control_id, depth }
  const [pendingNodes, setPendingNodes] = useState([]);

  const [controlName, setControlName] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [remediationSteps, setRemediationSteps] = useState('');

  const resetAll = () => {
    setActiveLevels(1);
    setLevelState(
      Array.from({ length: MAX_LEVELS }, () => ({
        mode: 'select',
        selectedKey: '',
        inputValue: '',
      }))
    );
    setCustomChildrenByParent({});
    setPendingNodes([]);
    setControlName('');
    setRecommendation('');
    setRemediationSteps('');
  };

  const handleClose = () => {
    resetAll();
    onHide();
  };

  const getParentKeyForLevel = (levelIdx, nextLevelState = levelState) => {
    if (levelIdx === 0) return ROOT_KEY;

    const prev = nextLevelState[levelIdx - 1];
    if (prev.mode === 'select') return prev.selectedKey || ROOT_KEY;

    const v = safeTrim(prev.inputValue);
    return v ? `new:${levelIdx}:${v}` : ROOT_KEY;
  };

  const buildOptionsFromTreeChildren = (parentNode) => {
    const children = Array.isArray(parentNode?.children) ? parentNode.children : [];
    return children.map((n) => ({
      key: String(n.control_id),
      label: `${String(n.control_id || '')} ${String(n.control_name || '')}`.trim(),
      node: n,
    }));
  };

  const getOptionsForLevel = (levelIdx) => {
    if (levelIdx === 0) return rootOptions || [];

    const parentKey = getParentKeyForLevel(levelIdx);

    if (String(parentKey).startsWith('new:')) {
      return customChildrenByParent[parentKey] || [];
    }

    const pathKeys = [];
    for (let i = 0; i < levelIdx; i += 1) {
      const s = levelState[i];
      if (s.mode === 'select') {
        if (!s.selectedKey) break;
        pathKeys.push(s.selectedKey);
      } else {
        const v = safeTrim(s.inputValue);
        if (!v) break;
        pathKeys.push(`new:${i + 1}:${v}`);
      }
    }

    const parentOption = getNodeByPath({ rootOptions, pathKeys });
    const treeOptions = parentOption ? buildOptionsFromTreeChildren(parentOption.node) : [];
    const customOptions = customChildrenByParent[parentKey] || [];

    const seen = new Set();
    const merged = [];
    for (const o of [...treeOptions, ...customOptions]) {
      if (seen.has(o.key)) continue;
      seen.add(o.key);
      merged.push(o);
    }
    return merged;
  };

  const decideModeForNextLevel = (levelIdx) => {
    const parentKey = getParentKeyForLevel(levelIdx);
    const opts = getOptionsForLevel(levelIdx);

    if (String(parentKey).startsWith('new:')) return 'input';
    return (opts || []).length > 0 ? 'select' : 'input';
  };

  const clearFromLevel = (fromIdx) => {
    setLevelState((prev) => {
      const next = [...prev];
      for (let i = fromIdx; i < MAX_LEVELS; i += 1) {
        next[i] = { mode: 'select', selectedKey: '', inputValue: '' };
      }
      return next;
    });
  };

  const handleSelectChange = (levelIdx, key) => {
    setLevelState((prev) => {
      const next = [...prev];
      next[levelIdx] = { ...next[levelIdx], selectedKey: key, mode: 'select' };
      return next;
    });

    const nextLevelIdx = levelIdx + 1;
    if (nextLevelIdx < MAX_LEVELS) {
      clearFromLevel(nextLevelIdx);
      setLevelState((prev) => {
        const next = [...prev];
        next[nextLevelIdx] = {
          ...next[nextLevelIdx],
          mode: decideModeForNextLevel(nextLevelIdx),
        };
        return next;
      });
    }
  };

  const handleInputChange = (levelIdx, value) => {
    setLevelState((prev) => {
      const next = [...prev];
      next[levelIdx] = { ...next[levelIdx], inputValue: value, mode: 'input' };
      return next;
    });

    const nextLevelIdx = levelIdx + 1;
    if (nextLevelIdx < MAX_LEVELS) {
      clearFromLevel(nextLevelIdx);
      setLevelState((prev) => {
        const next = [...prev];
        next[nextLevelIdx] = {
          ...next[nextLevelIdx],
          mode: 'input',
        };
        return next;
      });
    }
  };

  const handleConfirmAddOption = (levelIdx, name) => {
    const v = safeTrim(name);
    if (!v) return;

    const parentKey = getParentKeyForLevel(levelIdx);
    const tempId = newTempId();

    const newOption = {
      key: `custom:${tempId}`,
      label: `${`custom:${tempId}`} ${v}`.trim(),
      node: {
        control_id: `custom:${tempId}`,
        control_name: v,
        children: [],
      },
    };

    setCustomChildrenByParent((prev) => {
      const list = prev[parentKey] || [];
      return { ...prev, [parentKey]: [...list, newOption] };
    });

    // 把自訂節點記成 pending node，讓第一層 bulk 能先建立它
    setPendingNodes((prev) => [
      ...prev,
      {
        client_key: newOption.key,
        control_name: v,
        parent_control_id: parentKey === ROOT_KEY ? null : parentKey,
        depth: levelIdx + 1,
      },
    ]);

    handleSelectChange(levelIdx, newOption.key);
  };

  const canAddNextLevel = useMemo(() => {
    if (activeLevels >= MAX_LEVELS) return false;

    const lastIdx = activeLevels - 1;
    const last = levelState[lastIdx];

    if (last.mode === 'select') return Boolean(last.selectedKey);
    return Boolean(safeTrim(last.inputValue));
  }, [activeLevels, levelState]);

  const addLevel = () => {
    if (!canAddNextLevel) return;
    if (activeLevels >= MAX_LEVELS) return;

    const nextIdx = activeLevels;
    setActiveLevels((v) => Math.min(MAX_LEVELS, v + 1));

    setLevelState((prev) => {
      const next = [...prev];
      next[nextIdx] = { ...next[nextIdx], mode: decideModeForNextLevel(nextIdx) };
      return next;
    });
  };

  const removeLevel = () => {
    if (activeLevels <= 1) return;
    const removeIdx = activeLevels - 1;
    setActiveLevels((v) => Math.max(1, v - 1));
    clearFromLevel(removeIdx);
  };

  const getCurrentPathKeys = () => {
    const keys = [];
    for (let i = 0; i < activeLevels; i += 1) {
      const s = levelState[i];
      if (s.mode === 'select') {
        if (s.selectedKey) keys.push(s.selectedKey);
        else break;
      } else {
        const v = safeTrim(s.inputValue);
        if (v) keys.push(`new:${i + 1}:${v}`);
        else break;
      }
    }
    return keys;
  };

  const getCurrentPathLabels = () => {
    const labels = [];
    for (let i = 0; i < activeLevels; i += 1) {
      const s = levelState[i];
      if (s.mode === 'select') {
        const key = s.selectedKey;
        if (!key) break;

        const opts = getOptionsForLevel(i);
        const found = (opts || []).find((o) => o.key === key);
        labels.push(found?.label || key);
      } else {
        const v = safeTrim(s.inputValue);
        if (!v) break;
        labels.push(v);
      }
    }
    return labels;
  };

  const validate = () => {
    if (!safeTrim(controlName)) return { ok: false, message: '條款名稱為必填' };

    const first = levelState[0];
    if (first.mode === 'select' && !first.selectedKey) return { ok: false, message: '請先選擇階層一' };
    if (first.mode === 'input' && !safeTrim(first.inputValue)) return { ok: false, message: '請先輸入階層一' };

    const pathKeys = getCurrentPathKeys();
    if (pathKeys.length === 0) return { ok: false, message: '請至少完成一層階層選擇' };

    return { ok: true, message: '' };
  };

  const submit = () => {
    const v = validate();
    if (!v.ok) {
      // eslint-disable-next-line no-alert
      alert(v.message);
      return;
    }

    const path = getCurrentPathKeys();
    const pathLabels = getCurrentPathLabels();
    const parent_control_id = path[path.length - 1];

    onSubmit({
      temp_id: initialDraft?.temp_id || newTempId(),
      path,
      path_labels: pathLabels,
      parent_control_id,
      // 把 pendingNodes 帶回第一層
      custom_nodes: pendingNodes,
      control_name: safeTrim(controlName),
      recommendation: safeTrim(recommendation),
      remediation_steps: safeTrim(remediationSteps),
    });

    resetAll();
  };

  useEffect(() => {
    if (!show) return;

    if (!initialDraft) {
      resetAll();
      return;
    }

    setControlName(initialDraft.control_name || '');
    setRecommendation(initialDraft.recommendation || '');
    setRemediationSteps(initialDraft.remediation_steps || '');

    const draftPath = Array.isArray(initialDraft.path) ? initialDraft.path.slice(0, MAX_LEVELS) : [];
    const draftLabels = Array.isArray(initialDraft.path_labels) ? initialDraft.path_labels.slice(0, MAX_LEVELS) : [];

    const nextLevelState = Array.from({ length: MAX_LEVELS }, () => ({
      mode: 'select',
      selectedKey: '',
      inputValue: '',
    }));

    const nextCustom = {};

    for (let i = 0; i < draftPath.length; i += 1) {
      const k = String(draftPath[i] || '');
      const label = String(draftLabels[i] || '');

      const parsedNew = parseNewKey(k);
      if (parsedNew) {
        nextLevelState[i].mode = 'input';
        nextLevelState[i].inputValue = parsedNew.value || '';
        nextLevelState[i].selectedKey = '';
        continue;
      }

      nextLevelState[i].mode = 'select';
      nextLevelState[i].selectedKey = k;
      nextLevelState[i].inputValue = '';

      if (k.startsWith('custom:')) {
        let parentKey = ROOT_KEY;
        if (i > 0) {
          const prev = nextLevelState[i - 1];
          if (prev.mode === 'select') parentKey = prev.selectedKey || ROOT_KEY;
          else parentKey = `new:${i}:${safeTrim(prev.inputValue) || ''}`;
        }

        if (!nextCustom[parentKey]) nextCustom[parentKey] = [];
        nextCustom[parentKey].push({
          key: k,
          label: label || k,
          node: {
            control_id: k,
            control_name: label || k,
            children: [],
          },
        });
      }
    }

    setCustomChildrenByParent(nextCustom);

    // 編輯草稿時把 custom_nodes 帶回（沒有就空陣列）
    setPendingNodes(Array.isArray(initialDraft.custom_nodes) ? initialDraft.custom_nodes : []);

    setLevelState(nextLevelState);
    setActiveLevels(Math.max(1, draftPath.length || 1));
  }, [show, initialDraft]);

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '60%' }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <h4 style={{ margin: 0 }}>新增條款細項</h4>
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        {/* 內容區（可捲動） */}
        <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
          <Card>
            <Card.Body>
              {Array.from({ length: activeLevels }).map((_, idx) => {
                const s = levelState[idx];
                const options = s.mode === 'select' ? getOptionsForLevel(idx) : [];

                return (
                  <FSCLevelPickerRow
                    key={`lvl-${idx}`}
                    label={`階層${idx + 1}`}
                    mode={s.mode}
                    options={options}
                    selectedKey={s.selectedKey}
                    inputValue={s.inputValue}
                    onSelectChange={(key) => handleSelectChange(idx, key)}
                    onInputChange={(val) => handleInputChange(idx, val)}
                    onConfirmAddOption={(name) => handleConfirmAddOption(idx, name)}
                    showLevelActions={idx === activeLevels - 1}
                    disableAddLevel={!canAddNextLevel || activeLevels >= MAX_LEVELS}
                    disableRemoveLevel={activeLevels <= 1}
                    onAddLevel={addLevel}
                    onRemoveLevel={removeLevel}
                  />
                );
              })}

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold' }}>條款名稱</Form.Label>
                <Form.Control
                  value={controlName}
                  onChange={(e) => setControlName(e.target.value)}
                  placeholder="請輸入條款名稱"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold' }}>建議（非必填）</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={recommendation}
                  onChange={(e) => setRecommendation(e.target.value)}
                  placeholder="可留空"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold' }}>補救步驟（非必填）</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={remediationSteps}
                  onChange={(e) => setRemediationSteps(e.target.value)}
                  placeholder="可留空"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1px solid #e5e5e5',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#fff',
          }}
        >
          <Button className="btn btn-cancel" variant="secondary" onClick={handleClose}>
            取消
          </Button>
          <Button className="btn btn-custom" variant="success" onClick={submit}>
            {isEditing ? '更新' : '確認新增'}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
